import React, {
    useCallback,
    useEffect,
    useRef,
} from 'react';
import { useAuth } from '../../context/auth-context';
import { getEnvironmentVariable } from '../../utils';

const platformURL = getEnvironmentVariable(
    'REACT_APP_PLATFORM_URL',
);

const AUTH_URL = `${platformURL}/auth`;
const platformOrigin = new URL(platformURL).origin;
const AUTH_TIMEOUT = 4000;

type AuthContextValue = ReturnType<typeof useAuth>;

type AuthUser = Parameters<
    AuthContextValue['setUserInfo']
>[0];

type AuthMessageData = {
    event?: string;
    data?: {
        token?: string;
        user?: AuthUser | null;
    };
};

const SessionTokenIframe = () => {
    const {
        tokens,
        setUserInfo,
        setSessionToken,
        setSessionTokenLoading,
    } = useAuth();

    const iframeRef = useRef<HTMLIFrameElement>(null);

    const timeoutRef = useRef<
        ReturnType<typeof setTimeout> | null
    >(null);

    const finishedRef = useRef(false);

    const clearAuthTimeout = useCallback(() => {
        if (!timeoutRef.current) {
            return;
        }

        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
    }, []);

    const finishSessionCheck = useCallback(() => {
        if (finishedRef.current) {
            return;
        }

        finishedRef.current = true;
        clearAuthTimeout();
        setSessionTokenLoading(false);
    }, [
        clearAuthTimeout,
        setSessionTokenLoading,
    ]);

    const getAuthEventListener = useCallback((
        event: MessageEvent<AuthMessageData>,
    ) => {
        if (finishedRef.current) {
            return;
        }

        if (event.origin !== platformOrigin) {
            return;
        }

        if (
            event.source !==
            iframeRef.current?.contentWindow
        ) {
            return;
        }

        if (event.data?.event !== 'answer') {
            return;
        }

        const {
            token: sessionToken,
            user,
        } = event.data.data || {};

        if (
            localStorage.getItem('SHOULD_NOT_AUTH') ||
            !sessionToken ||
            !user
        ) {
            finishSessionCheck();
            return;
        }

        localStorage.setItem(
            'sessionToken',
            sessionToken,
        );

        localStorage.setItem(
            'user',
            JSON.stringify(user),
        );

        /*
         * setSessionToken updates authTokenProvider synchronously.
         * Only after that do we finish loading and render routes.
         */
        setSessionToken(sessionToken);
        setUserInfo(user);

        finishSessionCheck();
    }, [
        finishSessionCheck,
        setSessionToken,
        setUserInfo,
    ]);

    useEffect(() => {
        if (tokens.sessionToken) {
            finishSessionCheck();
            return;
        }

        finishedRef.current = false;
        setSessionTokenLoading(true);

        window.addEventListener(
            'message',
            getAuthEventListener,
        );

        return () => {
            clearAuthTimeout();

            window.removeEventListener(
                'message',
                getAuthEventListener,
            );
        };
    }, [
        tokens.sessionToken,
        clearAuthTimeout,
        finishSessionCheck,
        getAuthEventListener,
        setSessionTokenLoading,
    ]);

    const onIframeLoad = () => {
        if (finishedRef.current) {
            return;
        }

        iframeRef.current?.contentWindow?.postMessage(
            {
                event: 'getAuth',
            },
            platformOrigin,
        );

        clearAuthTimeout();

        /*
         * Start timeout only after the iframe has loaded
         * and the getAuth message has been sent.
         */
        timeoutRef.current = setTimeout(() => {
            finishSessionCheck();
        }, AUTH_TIMEOUT);
    };

    const onIframeError = () => {
        finishSessionCheck();
    };

    if (tokens.sessionToken) {
        return null;
    }

    return (
        <iframe
            ref={iframeRef}
            src={AUTH_URL}
            title="Authentication"
            onLoad={onIframeLoad}
            onError={onIframeError}
            style={{
                display: 'none',
            }}
        />
    );
};

export default SessionTokenIframe;